// Admin > Users / Vendors: send a user a WhatsApp, SMS or email message. WhatsApp and SMS go out
// only with a provider-approved template (Admin > Messaging Templates); email may also be written
// here. Every message is recorded in Admin > Audit Logs > Messages; the latest ones are listed below.
import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Alert, Typography, Stack, Tabs, Tab, TextField, MenuItem, Paper, Chip,
  LinearProgress, Divider,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import SmsIcon from '@mui/icons-material/Sms';
import EmailIcon from '@mui/icons-material/Email';
import SendIcon from '@mui/icons-material/Send';
import { USER_CONTACT_QUERY, MESSAGE_TEMPLATES_QUERY, SEND_USER_MESSAGE_MUTATION, MESSAGE_LOGS_QUERY } from '../graphql/queries';

const CHANNELS = [
  { key: 'whatsapp', label: 'WhatsApp', icon: <WhatsAppIcon sx={{ color: '#25D366' }} /> },
  { key: 'sms', label: 'SMS', icon: <SmsIcon color="primary" /> },
  { key: 'email', label: 'Email', icon: <EmailIcon color="secondary" /> },
];
const STATUS_COLOR = { sent: 'success', failed: 'error', held: 'warning', manual: 'info', skipped: 'default' };
const FREE = 'free';
const fmt = (iso) => new Date(iso).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
// Templates that send a temporary password are sent from Marketing / Change password, not here
const needsPassword = (t) => (t.variables || []).some(v => v.field === 'password') || /\{\{\s*password\s*\}\}/.test(t.body || '');

export default function SendMessageDialog({ userId, onClose }) {
  const [channel, setChannel] = useState('whatsapp');
  const [templateId, setTemplateId] = useState('');
  const [to, setTo] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [result, setResult] = useState(null);
  const { data: contactData, loading: contactLoading, error: contactError } = useQuery(USER_CONTACT_QUERY, { variables: { userId }, fetchPolicy: 'network-only' });
  const { data: tplData, loading: tplLoading } = useQuery(MESSAGE_TEMPLATES_QUERY, { variables: { channel }, fetchPolicy: 'cache-and-network' });
  const { data: logData, refetch: refetchLog } = useQuery(MESSAGE_LOGS_QUERY, { variables: { filter: { recipientUserId: userId }, limit: 5 }, fetchPolicy: 'network-only' });
  const [send, { loading: sending, error: sendError, reset }] = useMutation(SEND_USER_MESSAGE_MUTATION);
  const contact = contactData?.userContact;
  const templates = useMemo(() => (tplData?.messageTemplates || []).filter(t => t.sendable && !needsPassword(t)), [tplData]);
  const chosen = templates.find(t => t.id === templateId);

  // The recipient follows the channel: the user's email, or their mobile
  useEffect(() => {
    if (contact) setTo(channel === 'email' ? contact.email : (contact.phone || ''));
    setTemplateId(channel === 'email' ? FREE : '');
    setResult(null); reset();
  }, [channel, contact, reset]);

  const freeEmail = channel === 'email' && templateId === FREE;
  const ready = to.trim() && (freeEmail ? subject.trim() && body.trim() : Boolean(chosen));
  const go = async () => {
    const input = { userId, channel, to: to.trim(), ...(freeEmail ? { subject, body } : { templateId }) };
    const { data } = await send({ variables: { input } }).catch(() => ({}));
    if (data) { setResult(data.sendUserMessage); refetchLog(); }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Send a message{contact ? ` to ${contact.businessName || contact.name || contact.email}` : ''}</DialogTitle>
      <DialogContent dividers>
        {contactLoading && <LinearProgress />}
        {contactError && <Alert severity="error">{contactError.message}</Alert>}
        <Tabs value={channel} onChange={(_, v) => setChannel(v)} sx={{ mb: 2 }} variant="fullWidth">
          {CHANNELS.map(c => <Tab key={c.key} value={c.key} icon={c.icon} iconPosition="start" label={c.label} sx={{ minHeight: 48 }} />)}
        </Tabs>
        <Stack spacing={2}>
          <TextField size="small" label={channel === 'email' ? 'Email address' : 'Mobile number (with country code)'} value={to} onChange={e => setTo(e.target.value)} fullWidth
            helperText={channel !== 'email' && contact && !contact.phone ? 'No mobile number is saved for this user: enter one' : ' '} />
          <TextField select size="small" label="Template" value={templateId} onChange={e => setTemplateId(e.target.value)} fullWidth
            helperText={tplLoading ? 'Loading templates…' : channel !== 'email' && !templates.length
              ? `No approved ${channel === 'sms' ? 'SMS' : 'WhatsApp'} templates yet (Admin → Messaging Templates)` : 'Only approved templates can be sent'}>
            {channel === 'email' && <MenuItem value={FREE}><em>Write my own email</em></MenuItem>}
            {templates.map(t => <MenuItem key={t.id} value={t.id}>{t.name} · {t.language}</MenuItem>)}
          </TextField>
          {freeEmail && (
            <>
              <TextField size="small" label="Subject" value={subject} onChange={e => setSubject(e.target.value)} fullWidth inputProps={{ maxLength: 300 }} />
              <TextField label="Message" value={body} onChange={e => setBody(e.target.value)} fullWidth multiline minRows={5} />
            </>
          )}
          {chosen && (
            <Paper variant="outlined" sx={{ p: 1.5, bgcolor: 'grey.50' }}>
              <Typography variant="caption" color="text.secondary">Template text (the app fills in the user's name, business, sign-in link…)</Typography>
              {[...(chosen.variables || []), ...(chosen.buttons || [])].some(v => ['password_link', 'password_token'].includes(v.field)) && (
                <Alert severity="info" sx={{ my: 1 }}>A new one-time link to set their password is created and sent (valid 3 days; earlier links stop working once it is used).</Alert>
              )}
              {chosen.subject && <Typography variant="body2" fontWeight={600}>{chosen.subject}</Typography>}
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', maxHeight: 180, overflow: 'auto' }}>
                {channel === 'email' ? chosen.body.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() : chosen.body}
              </Typography>
            </Paper>
          )}
          {sendError && <Alert severity="error">{sendError.message}</Alert>}
          {result && <Alert severity="success">{result}</Alert>}
        </Stack>

        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" mb={1}>Recent messages to this user</Typography>
        {!(logData?.messageLogs?.rows || []).length && <Typography variant="body2" color="text.secondary">None yet.</Typography>}
        {(logData?.messageLogs?.rows || []).map(m => (
          <Stack key={m.id} direction="row" spacing={1} alignItems="center" mb={0.75} flexWrap="wrap" useFlexGap>
            <Chip size="small" label={m.channel} />
            <Chip size="small" variant="outlined" label={m.status} color={STATUS_COLOR[m.status] || 'default'} />
            <Typography variant="caption" color="text.secondary">{fmt(m.createdAt)}</Typography>
            <Typography variant="caption" noWrap sx={{ maxWidth: 260 }}>{m.subject || m.templateName || m.purpose}</Typography>
          </Stack>
        ))}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button variant="contained" startIcon={<SendIcon />} disabled={!ready || sending} onClick={go}>{sending ? 'Sending…' : 'Send'}</Button>
      </DialogActions>
    </Dialog>
  );
}
